/* ============================================================
   Photozyme — Buy Box (Recharge)
   Namespace: pz-buybox

   Behaviour for the server-rendered buy box in
   snippets/pz-buybox-recharge.liquid.

   The UI is already on the page when this runs — Liquid rendered it
   with the right prices, the right plan list and the subscription
   card pre-selected. This script only handles interaction:

     - Subscribe / One Time selection
     - the "Deliver every" dropdown
     - writing the chosen plan into the form's
       input[name="selling_plan"] (empty = one-time), which is all
       Recharge needs on this store (Shopify Checkout Integration)
     - re-pricing everything when the shopper switches variant
     - the enlarged product title from the approved design

   There is no app widget and no shadow DOM in the loop, so nothing
   here can be knocked out by an app re-render.
   ============================================================ */

(function () {
  "use strict";

  if (window.PZBuyBoxRecharge) return; // the asset can be requested by more than one section

  var NS = "pz-buybox";
  var HERO_TITLE_SELECTOR = "h1.product-description-header";
  var VARIANT_SELECTOR = 'select[name="id"], input[name="id"]';

  /* ---------- money ---------- */

  function formatNumber(cents, decimals, thousandSep, decimalSep) {
    var n = (cents / 100).toFixed(decimals);
    var parts = n.split(".");
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
    return parts[1] ? whole + decimalSep + parts[1] : whole;
  }

  /* Renders cents through the shop's own money format, so the box reads
     the same as every other price on the page in every market. */
  function formatMoney(cents, format) {
    var fmt = format || "${{amount}}";
    var match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var token = match ? match[1] : "amount";
    var value;

    switch (token) {
      case "amount_no_decimals":
        value = formatNumber(cents, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        value = formatNumber(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatNumber(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatNumber(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatNumber(cents, 0, " ", ",");
        break;
      case "amount_with_space_separator":
        value = formatNumber(cents, 2, " ", ",");
        break;
      default:
        value = formatNumber(cents, 2, ",", ".");
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* ---------- one buy box ---------- */

  function BuyBox(root) {
    this.root = root;
    this.data = this.readData();
    if (!this.data) return;

    this.input = this.el("plan-input");
    this.select = this.el("select");
    this.subCard = this.el("card-sub");
    this.otCard = this.el("card-onetime");
    if (!this.input || !this.select || !this.subCard || !this.otCard) return;

    this.variantId = this.currentVariantId();
    this.bind();
    this.ready = true;
  }

  BuyBox.prototype.el = function (key) {
    return this.root.querySelector('[data-pz="' + key + '"]');
  };

  BuyBox.prototype.readData = function () {
    var node = this.root.querySelector("[data-pz-buybox-data]");
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      if (window.console && console.warn) console.warn("[pz-buybox] bad data island", err);
      return null;
    }
  };

  /* The theme keeps the authoritative variant in the form's own
     select[name="id"]; the visible option dropdowns only feed it. */
  BuyBox.prototype.form = function () {
    var id = this.root.getAttribute("data-pz-form");
    return (id && document.getElementById(id)) || this.root.closest("form");
  };

  BuyBox.prototype.currentVariantId = function () {
    var form = this.form();
    var field = form && form.querySelector(VARIANT_SELECTOR);
    var id = field && field.value;
    if (id && this.data.variants[id]) return String(id);
    var keys = Object.keys(this.data.variants);
    return keys.length ? keys[0] : null;
  };

  BuyBox.prototype.variant = function () {
    return (this.variantId && this.data.variants[this.variantId]) || null;
  };

  BuyBox.prototype.isSubscribed = function () {
    return this.subCard.classList.contains("is-selected");
  };

  BuyBox.prototype.planPrice = function (planId) {
    var v = this.variant();
    return v && v.plans ? v.plans[planId] : null;
  };

  BuyBox.prototype.bind = function () {
    var self = this;

    var activate = function (card, subscribe) {
      return function (e) {
        var t = e.target;
        if (t && t.closest) {
          if (t.closest("select")) return;
          // the ⓘ badge only swallows the click once the card is selected,
          // so the tooltip stays readable without making it a dead target
          if (t.closest("." + NS + "__info") && card.classList.contains("is-selected")) return;
        }
        if (e.type === "keydown") {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
        }
        self.setSubscribed(subscribe);
      };
    };

    this.subCard.addEventListener("click", activate(this.subCard, true));
    this.subCard.addEventListener("keydown", activate(this.subCard, true));
    this.otCard.addEventListener("click", activate(this.otCard, false));
    this.otCard.addEventListener("keydown", activate(this.otCard, false));

    this.select.addEventListener("change", function () {
      self.render();
      self.emit();
    });
  };

  BuyBox.prototype.setSubscribed = function (on) {
    this.subCard.classList.toggle("is-selected", on);
    this.subCard.setAttribute("aria-checked", on ? "true" : "false");
    this.otCard.classList.toggle("is-selected", !on);
    this.otCard.setAttribute("aria-checked", !on ? "true" : "false");
    this.render();
    this.emit();
  };

  /* Called on every variant change: re-price the cards, the dropdown
     labels and the savings copy from the data island. */
  BuyBox.prototype.render = function () {
    var v = this.variant();
    if (!v) return;

    var fmt = this.data.moneyFormat;
    var oneTime = v.price;
    var planId = this.select.value;
    var subPrice = this.planPrice(planId);

    // A variant can be missing an allocation for the selected plan; fall
    // back to the first plan that this variant actually supports.
    if (subPrice == null) {
      for (var i = 0; i < this.data.planIds.length; i++) {
        var alt = this.data.planIds[i];
        if (this.planPrice(alt) != null) {
          planId = alt;
          this.select.value = alt;
          subPrice = this.planPrice(alt);
          break;
        }
      }
    }

    var options = this.select.options;
    var best = 0;
    for (var j = 0; j < options.length; j++) {
      var opt = options[j];
      var price = this.planPrice(opt.value);
      var label = this.data.labels[opt.value] || opt.textContent;
      if (price == null) {
        opt.disabled = true;
        opt.textContent = label;
        continue;
      }
      opt.disabled = false;
      var pct = oneTime > 0 ? Math.round(((oneTime - price) / oneTime) * 100) : 0;
      opt.textContent = pct > 0 ? label + " with " + pct + "% discount" : label;
      if (pct > best) best = pct;
    }

    var subscribed = this.isSubscribed();
    this.input.value = subscribed && subPrice != null ? planId : "";

    var text = function (node, value) {
      if (node) node.textContent = value;
    };

    if (subPrice != null) {
      var perOrder = Math.max(0, oneTime - subPrice);
      var pctOrder = oneTime > 0 ? Math.round((perOrder / oneTime) * 100) : 0;
      var perYear = Math.round(perOrder * (this.data.ordersPerYear[planId] || 12));

      text(this.el("sub-title"), "Subscribe & Save " + formatMoney(perOrder, fmt) + " per order");
      text(this.el("sub-compare"), formatMoney(oneTime, fmt));
      text(this.el("sub-price"), formatMoney(subPrice, fmt));
      text(this.el("benefit-order"), "Save " + pctOrder + "% per order");
      text(this.el("benefit-year"), "Save " + formatMoney(perYear, fmt) + " per year");
    }

    text(this.el("ot-price"), formatMoney(oneTime, fmt));

    var pill = this.el("pill");
    if (pill) {
      pill.textContent = "Save " + best + "%";
      pill.style.display = best > 0 ? "" : "none";
    }
  };

  /* Lets other scripts (analytics, upsells) see the current choice. */
  BuyBox.prototype.emit = function () {
    var detail = {
      variantId: this.variantId,
      sellingPlanId: this.input.value || null,
      subscribed: this.isSubscribed()
    };
    this.root.dispatchEvent(new CustomEvent("pz:buybox:change", { bubbles: true, detail: detail }));
  };

  BuyBox.prototype.syncVariant = function () {
    var id = this.currentVariantId();
    if (id === this.variantId) return;
    this.variantId = id;
    this.render();
    this.emit();
  };

  /* ---------- boot ---------- */

  var boxes = [];

  function init() {
    document.querySelectorAll("[data-pz-buybox]").forEach(function (root) {
      if (root.hasAttribute("data-pz-ready")) return;
      var box = new BuyBox(root);
      if (!box.ready) return;
      root.setAttribute("data-pz-ready", "");
      boxes.push(box);
      box.render();
    });

    document.querySelectorAll(HERO_TITLE_SELECTOR).forEach(function (h1) {
      h1.classList.add("pz-hero-title");
    });
  }

  /* The theme swaps variants through jQuery and updates select[name="id"]
     after its own change handlers run, so read it back on the next tick
     rather than trusting the event target. */
  var pending = null;
  document.addEventListener(
    "change",
    function () {
      clearTimeout(pending);
      pending = setTimeout(function () {
        boxes.forEach(function (b) {
          b.syncVariant();
        });
      }, 60);
    },
    true
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Theme editor: the section is re-rendered wholesale on every change.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener("shopify:section:load", function () {
      boxes = [];
      init();
    });
  }

  window.PZBuyBoxRecharge = { init: init, boxes: boxes, formatMoney: formatMoney };
})();
